import { shouldUseSameSiteNone, isSecureContext } from './iframeSecurity';

/**
 * Critical tokens that need localStorage fallback in iframe scenarios
 */
const CRITICAL_TOKENS = ['user', 'refresh'];

export function removeCookie(cname: string): void {
  const expires = 'expires=Thu, 01 Jan 1970 00:00:00 UTC;';
  const secure = isSecureContext();

  // Try to remove cookie with appropriate SameSite attribute
  if (shouldUseSameSiteNone() && secure) {
    document.cookie = `${cname}=;${expires};path=/;SameSite=None;Secure`;
  } else {
    document.cookie = `${cname}=;${expires};path=/`;
  }

  // Also remove from localStorage if it's a critical token
  if (CRITICAL_TOKENS.includes(cname)) {
    try {
      localStorage.removeItem(`cookie_${cname}`);
    } catch (e) {
      console.warn(`Failed to remove localStorage fallback for ${cname}:`, e);
    }
  }
}

/**
 * Store token in localStorage with expiration
 */
function storeTokenInLocalStorage(cname: string, cvalue: string, expirationTime: number): void {
  try {
    localStorage.setItem(
      `cookie_${cname}`,
      JSON.stringify({
        value: cvalue,
        expires: expirationTime,
      }),
    );
  } catch (e) {
    console.warn(`Failed to set localStorage fallback for ${cname}:`, e);
  }
}

export function setCookie(cname: string, cvalue: string, hours = 9600): void {
  const d = new Date();
  d.setTime(d.getTime() + hours * 60 * 60 * 1000);
  const expires = `expires=${d.toUTCString()}`;
  const secure = isSecureContext();
  const expirationTime = Date.now() + hours * 60 * 60 * 1000;

  // Set cookie with appropriate SameSite attribute
  let cookieString = `${cname}=${cvalue};${expires};path=/`;

  // In iframe context with allowed parent origin, we need SameSite=None; Secure for cookies to work
  if (shouldUseSameSiteNone() && secure) {
    cookieString += ';SameSite=None;Secure';
  }

  try {
    document.cookie = cookieString;

    // For critical tokens, also store in localStorage as fallback
    if (CRITICAL_TOKENS.includes(cname)) {
      storeTokenInLocalStorage(cname, cvalue, expirationTime);
    }
  } catch (e) {
    console.warn(`Failed to set cookie ${cname}:`, e);
    // If cookie fails and it's a critical token, ensure localStorage is set
    if (CRITICAL_TOKENS.includes(cname)) {
      storeTokenInLocalStorage(cname, cvalue, expirationTime);
    }
  }
}

export function getCookie(cname: string): string {
  const name = cname + '=';
  const ca = document.cookie.split(';');

  // First, try to get from cookies
  for (const element of ca) {
    let c = element;
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      const cookieValue = c.substring(name.length, c.length);
      // If we got a value from cookie, also sync it to localStorage for critical tokens
      if (cookieValue && CRITICAL_TOKENS.includes(cname)) {
        const expirationTime = Date.now() + 9600 * 60 * 60 * 1000; // Default expiration
        storeTokenInLocalStorage(cname, cookieValue, expirationTime);
      }
      return cookieValue;
    }
  }

  // If cookie is not found and it's a critical token, check localStorage fallback
  if (CRITICAL_TOKENS.includes(cname)) {
    try {
      const stored = localStorage.getItem(`cookie_${cname}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Check if the stored value has expired
        if (parsed.expires && parsed.expires > Date.now()) {
          return parsed.value;
        } else {
          // Expired, remove it
          localStorage.removeItem(`cookie_${cname}`);
        }
      }
    } catch (e) {
      console.warn(`Failed to get localStorage fallback for ${cname}:`, e);
    }
  }

  return '';
}

/**
 * Clear all cookies for the current host only (no parent-domain writes).
 * This avoids touching apex domains like "sprouts.ai".
 */
export function clearAllCookiesForCurrentHost(): void {
  if (!document.cookie) return;

  const cookies = document.cookie.split('; ');
  const pathParts = window.location.pathname.split('/');

  for (const cookie of cookies) {
    const cookieName = encodeURIComponent(cookie.split(';')[0].split('=')[0].trim());
    const cookieBase = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=`;

    // Clear at root path
    document.cookie = cookieBase + '/';

    // Clear at each path segment
    const parts = [...pathParts];
    while (parts.length > 0) {
      const path = parts.join('/') || '/';
      document.cookie = cookieBase + path;
      parts.pop();
    }
  }
}

export function checkCookie(): string | null {
  const user = getCookie('user');
  if (user !== '') {
    return user;
  } else {
    return null;
  }
}
